/**
 * @fileoverview Sign-in page. Clicking the button navigates to the backend
 * GitHub OAuth endpoint; on return, the backend-issued JWT in ?ct= is stored
 * to establish the session.
 */

import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import GitHubIcon from '@mui/icons-material/GitHub';
import { alpha, useTheme } from '@mui/material/styles';
import { Navigate, useNavigate } from 'react-router';
import { useAuthSession } from '../index';
import { signInWithJwt } from '../session/auth';
import { getRememberedCallbackUrl, getSafeCallbackUrl, rememberCallbackUrl } from '../utils/callbackUrl';
import { BrandingContext, RouterContext } from '@/app/contexts';
import { API_BASE_URL } from '@/config/app';
import { logLogin } from '@/api/login';

/** Dev-only credentials login is bundled only outside production builds. */
const DEV_LOGIN_ENABLED = import.meta.env.DEV;

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  invalid_state: 'Sign-in failed: invalid request state. Please try again.',
  oauth_failed: 'GitHub sign-in failed. Please try again.',
  server_error: 'Server error during sign-in. Please try again.',
  access_denied: 'Access denied: this GitHub account is not permitted.',
};

export default function SignIn() {
  const { session, setSession, loading: authLoading } = useAuthSession();
  const navigate = useNavigate();
  const theme = useTheme();
  const branding = React.useContext(BrandingContext);
  const router = React.useContext(RouterContext);

  const [{ loading, error }, setFormStatus] = React.useState<{
    loading: boolean;
    error?: string;
  }>({
    loading: false,
    error: '',
  });

  const [devUsername, setDevUsername] = React.useState('');
  const [devPassword, setDevPassword] = React.useState('');

  // Where to go once signed in: the page the app shell sent us here from, or,
  // when GitHub OAuth returns with ?ct= or ?error= (and none of our query
  // string), the page remembered before leaving for GitHub.
  const [destination] = React.useState(() => {
    const params = new URLSearchParams(window.location.search);
    const returningFromOAuth = params.has('ct') || params.has('error');
    return getSafeCallbackUrl(
      router?.searchParams.get('callbackUrl') ?? (returningFromOAuth ? getRememberedCallbackUrl() : null)
    );
  });

  // Shared: turn a backend-issued JWT into an active session and navigate on.
  const establishSession = React.useCallback(
    async (token: string): Promise<void> => {
      setFormStatus({ loading: true, error: '' });
      const result = await signInWithJwt(token);

      if (result.success && result.user) {
        logLogin(true).catch(console.warn);
        setSession({
          user: {
            name: result.user.displayName || '',
            email: result.user.email || '',
            image: result.user.photoURL || '',
          },
        });
        rememberCallbackUrl(null);
        navigate(destination, { replace: true });
        return;
      }

      setFormStatus({ loading: false, error: result.error || 'Sign-in failed. Please try again.' });
      logLogin(false, result.error || 'Token sign-in failed').catch(console.warn);
    },
    [navigate, setSession, destination]
  );

  // On mount: check for ?ct= (JWT from backend OAuth) or ?error= (OAuth failure)
  React.useEffect(() => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('ct');
    const authError = url.searchParams.get('error');

    if (!token && !authError) return;

    // Clean URL params so the token isn't visible in the address bar
    url.searchParams.delete('ct');
    url.searchParams.delete('error');
    window.history.replaceState({}, '', url.toString());

    if (authError) {
      setFormStatus({
        loading: false,
        error: AUTH_ERROR_MESSAGES[authError] ?? 'Sign-in failed. Please try again.',
      });
      logLogin(false, `OAuth error: ${authError}`).catch(console.warn);
      return;
    }

    if (!token) return;

    establishSession(token);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormStatus({ error: '', loading: true });
    // The OAuth round trip drops our query string, so keep the destination.
    rememberCallbackUrl(destination);
    window.location.href = '/api/auth/github';
  }

  async function handleDevLogin(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormStatus({ loading: true, error: '' });
    try {
      const res = await fetch(`${API_BASE_URL}/auth/dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: devUsername, password: devPassword }),
      });

      if (!res.ok) {
        setFormStatus({
          loading: false,
          error: res.status === 401
            ? 'Invalid development credentials.'
            : 'Dev login failed. Is the backend running with DB_DRIVER=pglite?',
        });
        return;
      }

      const data = (await res.json()) as { token: string };
      await establishSession(data.token);
    } catch {
      setFormStatus({ loading: false, error: 'Could not reach the dev login endpoint.' });
    }
  }

  if (authLoading) {
    return <LinearProgress />;
  }

  if (session) {
    return <Navigate to={destination} replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <title>Sign In | toolpad.keithwalsh.eu</title>
      <Container component="main" maxWidth="xs">
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: 'background.paper',
            borderRadius: 1,
            p: 4,
            gap: 1,
            border: '1px solid',
            borderColor: alpha(theme.palette.grey[400], 0.4),
            boxShadow: theme.shadows[4],
          }}
        >
          {branding?.logo}

          <Typography
            variant="h5"
            component="h1"
            color="textPrimary"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
            }}
          >
            Sign In
          </Typography>

          <Typography variant="body2" color="textSecondary" gutterBottom sx={{
            textAlign: "center"
          }}>
            toolpad.keithwalsh.eu
          </Typography>

          <Box sx={{ width: '100%', mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Button
                variant="outlined"
                type="submit"
                fullWidth
                size="large"
                disableElevation
                color="inherit"
                disabled={loading}
                startIcon={<GitHubIcon />}
                sx={{ textTransform: 'capitalize' }}
              >
                Sign in with GitHub
              </Button>
            </form>

            {DEV_LOGIN_ENABLED && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    Development login
                  </Typography>
                </Divider>

                <form onSubmit={handleDevLogin}>
                  <Stack spacing={2}>
                    <TextField
                      label="Username"
                      size="small"
                      fullWidth
                      autoComplete="username"
                      value={devUsername}
                      onChange={(e) => setDevUsername(e.target.value)}
                    />
                    <TextField
                      label="Password"
                      type="password"
                      size="small"
                      fullWidth
                      autoComplete="current-password"
                      value={devPassword}
                      onChange={(e) => setDevPassword(e.target.value)}
                    />
                    <Button
                      variant="contained"
                      type="submit"
                      fullWidth
                      disableElevation
                      disabled={loading}
                    >
                      Sign in (dev)
                    </Button>
                  </Stack>
                </form>
              </Box>
            )}
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
